import { Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import Fetch from "../services/Fetch";
import { useConstants } from "../hooks/UseConstants";

function SummaryCard({ icon, label, value, theme }) {
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    bgcolor: "#eff6ff",
                    color: "#0D358C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
            </Box>
        </Box>
    );
}

function StudentDetails({ onClickClose, student, onUpdated, setSnackBar }) {
    const { host, language } = useConstants();
    const today = new Date();
    const birthDate = student.birth_date ? new Date(student.birth_date) : null;
    const age = birthDate && !Number.isNaN(birthDate.getTime()) ? today.getFullYear() - birthDate.getFullYear() : "-";
    const [toggling, setToggling] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resettingPassword, setResettingPassword] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [firstName, setFirstName] = useState(student.first_name || "");
    const [lastName, setLastName] = useState(student.last_name || "");
    const [email, setEmail] = useState(student.email || "");
    const [phoneCode, setPhoneCode] = useState(student.phone_code || "");
    const [phone, setPhone] = useState(student.phone || "");
    const [majorId, setMajorId] = useState(student.major?.id ? String(student.major.id) : "");
    const [academicDegreeId, setAcademicDegreeId] = useState(student.academic_degree?.id ? String(student.academic_degree.id) : "");
    const [majors, setMajors] = useState([]);
    const [academicDegrees, setAcademicDegrees] = useState([]);

    const theme = useTheme();
    const intl = useIntl();
    const isAr = language === "ar";
    const educationType = student.major?.level === 'school'
        ? intl.formatMessage({ id: "school_education" })
        : intl.formatMessage({ id: "university_education" });
    const stageLabel = student.major?.level === 'school'
        ? intl.formatMessage({ id: "education_stage" })
        : intl.formatMessage({ id: "collage" });
    const stageValue = language === 'en' ? student.major?.name_en : student.major?.name_ar || "-";
    const statusLabel = student.is_active ? (isAr ? "مفعّل" : "Active") : (isAr ? "موقوف" : "Inactive");
    const formatDate = (value) => {
        if (!value) return isAr ? "غير متوفر" : "Unavailable";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
    };

    useEffect(() => {
        setFirstName(student.first_name || "");
        setLastName(student.last_name || "");
        setEmail(student.email || "");
        setPhoneCode(student.phone_code || "");
        setPhone(student.phone || "");
        setMajorId(student.major?.id ? String(student.major.id) : "");
        setAcademicDegreeId(student.academic_degree?.id ? String(student.academic_degree.id) : "");
    }, [student]);

    useEffect(() => {
        const loadOptions = async () => {
            const [majorsResult, degreesResult] = await Promise.all([
                Fetch(`${host}/majors?per_page=200`, 'GET', null),
                Fetch(`${host}/academic-degrees?per_page=100`, 'GET', null),
            ]);

            if (majorsResult.status === 200) {
                const items = majorsResult.data?.data?.data || majorsResult.data?.data || [];
                setMajors(Array.isArray(items) ? items : []);
            }
            if (degreesResult.status === 200) {
                const items = degreesResult.data?.data?.data || degreesResult.data?.data || [];
                setAcademicDegrees(Array.isArray(items) ? items : []);
            }
        };

        loadOptions();
    }, [host]);

    const toggleActive = async () => {
        setToggling(true);
        const result = await Fetch(`${host}/admin/users/${student.id}/active`, 'POST', {});
        if (result.status === 200) {
            const updated = { ...student, is_active: !student.is_active };
            onUpdated?.(updated);
            setSnackBar?.('success', isAr ? 'تم تحديث حالة الحساب' : 'Account status updated');
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setToggling(false);
    };

    const resetPassword = async () => {
        if (!newPassword || !confirmPassword) {
            setSnackBar?.('error', isAr ? 'أدخل كلمة المرور الجديدة وتأكيدها' : 'Enter the new password and confirmation');
            return;
        }

        if (newPassword !== confirmPassword) {
            setSnackBar?.('error', isAr ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
            return;
        }

        setResettingPassword(true);
        const formData = new FormData();
        formData.append('password', newPassword);
        formData.append('password_confirmation', confirmPassword);

        const result = await Fetch(`${host}/admin/users/${student.id}/password/update`, 'POST', formData);
        if (result.status === 200) {
            setNewPassword("");
            setConfirmPassword("");
            setSnackBar?.('success', isAr ? 'تمت إعادة تعيين كلمة المرور' : 'Password reset successfully');
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setResettingPassword(false);
    };

    const saveProfile = async () => {
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setSnackBar?.('error', isAr ? 'أكمل الاسم والبريد الإلكتروني أولاً' : 'Complete the name and email first');
            return;
        }

        setProfileSaving(true);
        const formData = new FormData();
        formData.append('first_name', firstName.trim());
        formData.append('last_name', lastName.trim());
        formData.append('email', email.trim());
        if (phoneCode !== '') formData.append('phone_code', phoneCode);
        if (phone !== '') formData.append('phone', phone);
        if (majorId !== '') formData.append('major_id', majorId);
        if (academicDegreeId !== '') formData.append('academic_degree_id', academicDegreeId);

        const result = await Fetch(`${host}/admin/users/${student.id}/update`, 'POST', formData);
        if (result.status === 200) {
            setSnackBar?.('success', isAr ? 'تم حفظ بيانات الطالب' : 'Student profile saved');
            onUpdated?.(result.data.data);
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setProfileSaving(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "94%", sm: "84%", md: "68%" },
                maxWidth: 920,
                maxHeight: "92vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id="student_details" /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "عرض بيانات الطالب الأكاديمية وبيانات التواصل بشكل أوضح." : "Review the student academic profile and contact information."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto" }} className="none-view-scroll">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#e2e8f0", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800 }}>
                            {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={900} sx={{ color: "#0D358C" }}>
                                {student.first_name} {student.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{student.email}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip label={educationType} variant="outlined" color="primary" />
                        <Chip label={statusLabel} variant="outlined" color={student.is_active ? "success" : "warning"} />
                    </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={intl.formatMessage({ id: "education_specialization" })} value={educationType} theme={theme} />
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={stageLabel} value={stageValue} theme={theme} />
                    <SummaryCard icon={<CalendarMonthOutlinedIcon />} label={intl.formatMessage({ id: "age" })} value={age} theme={theme} />
                    <SummaryCard icon={<AlternateEmailOutlinedIcon />} label={intl.formatMessage({ id: "email" })} value={student.email || "-"} theme={theme} />
                    <SummaryCard icon={<PhoneOutlinedIcon />} label={intl.formatMessage({ id: "phone" })} value={`${student.phone_code || ""} ${student.phone || ""}`.trim() || "-"} theme={theme} />
                    <SummaryCard icon={<CalendarMonthOutlinedIcon />} label={isAr ? "تاريخ الميلاد" : "Birth date"} value={formatDate(student.birth_date)} theme={theme} />
                    {student.major?.level === 'school' ? (
                        <SummaryCard icon={<SchoolOutlinedIcon />} label={intl.formatMessage({ id: "class" })} value={stageValue} theme={theme} />
                    ) : null}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box
                    sx={{
                        p: 2.25,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                        bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                    }}
                >
                    <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1 }}>
                        {isAr ? "ملخص أكاديمي" : "Academic summary"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {student.major?.level === 'school'
                            ? (isAr ? "الطالب مسجل ضمن المسار المدرسي، ويمكن استخدام بيانات المرحلة/الصف لفهم المحتوى المناسب له." : "The student is enrolled in the school track, and the stage/class information can guide suitable content.")
                            : (isAr ? "الطالب مسجل ضمن المسار الجامعي، ويمكن استخدام بيانات الكلية/التخصص لتخصيص المحتوى المناسب." : "The student is enrolled in the university track, and the college/major data can help tailor suitable content.")}
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box
                    sx={{
                        p: 2.25,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                        bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                        mb: 2,
                    }}
                >
                    <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                        {isAr ? "تعديل بيانات الطالب" : "Edit student profile"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "first_name" })} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "last_name" })} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "email" })} value={email} onChange={(e) => setEmail(e.target.value)} />
                        <TextField fullWidth size="small" label={isAr ? "رمز الدولة" : "Country code"} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "phone" })} value={phone} onChange={(e) => setPhone(e.target.value)} />
                        <TextField select fullWidth size="small" label={intl.formatMessage({ id: "academic_degree" })} value={academicDegreeId} onChange={(e) => setAcademicDegreeId(e.target.value)}>
                            <MenuItem value="">{isAr ? "بدون تحديد" : "Not set"}</MenuItem>
                            {academicDegrees.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{language === 'en' ? item.name_en : item.name_ar}</MenuItem>
                            ))}
                        </TextField>
                        <TextField select fullWidth size="small" label={stageLabel} value={majorId} onChange={(e) => setMajorId(e.target.value)} sx={{ gridColumn: { xs: "span 1", md: "span 2" } }}>
                            <MenuItem value="">{isAr ? "بدون تحديد" : "Not set"}</MenuItem>
                            {majors.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{language === 'en' ? item.name_en : item.name_ar}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {isAr ? "يمكنك تعديل بيانات الطالب الأساسية والأكاديمية مباشرة من هذه النافذة." : "You can update the student's basic and academic data directly from this dialog."}
                    </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <Box
                        sx={{
                            p: 2.25,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                        }}
                    >
                        <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                            {isAr ? "إعدادات الحساب" : "Account settings"}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label={isAr ? "حالة الحساب" : "Account status"}
                            value={statusLabel}
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={isAr ? "تاريخ الميلاد" : "Birth date"}
                            value={formatDate(student.birth_date)}
                            InputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box
                        sx={{
                            p: 2.25,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                        }}
                    >
                        <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                            {isAr ? "إعادة تعيين كلمة المرور" : "Reset password"}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            label={isAr ? "كلمة المرور الجديدة" : "New password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            label={isAr ? "تأكيد كلمة المرور" : "Confirm password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {isAr ? "يستخدم هذا الإجراء للدعم الإداري أو عند نسيان الطالب لكلمة المرور." : "Use this administrative action for support cases or when the student forgets their password."}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={saveProfile}
                        disabled={profileSaving}
                        startIcon={profileSaving ? <CircularProgress size={18} color="inherit" /> : <PersonOutlineOutlinedIcon />}
                        sx={{ minWidth: 180, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                    >
                        {isAr ? "حفظ بيانات الطالب" : "Save student profile"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={resetPassword}
                        disabled={resettingPassword}
                        startIcon={resettingPassword ? <CircularProgress size={18} color="inherit" /> : <PhoneOutlinedIcon />}
                        sx={{ minWidth: 190 }}
                    >
                        {isAr ? "حفظ كلمة المرور الجديدة" : "Save new password"}
                    </Button>
                    <Button
                        variant="outlined"
                        color={student.is_active ? "warning" : "success"}
                        onClick={toggleActive}
                        disabled={toggling}
                        startIcon={toggling ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ minWidth: 190 }}
                    >
                        {student.is_active
                            ? (isAr ? "إيقاف الحساب" : "Deactivate")
                            : (isAr ? "تفعيل الحساب" : "Activate account")}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default StudentDetails;